import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { DraggableProvided, DraggableStateSnapshot, DroppableProvided } from 'react-beautiful-dnd';
import { toast } from 'sonner';
import { CopyTemplateLinkModal } from '../../../../admin/containers/templates/CopyTemplateLinkModal';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { usePersistChangeInBrowserStorage } from '../../../../models/hooks/usePersistChangeInBrowserStorage';
import { useEventLogging } from '../../../../models/Logging';
import { HiddenDefaultMessageTemplateKey, hideDefaultMessageTemplate } from '../../../../models/MessageTemplates';
import { storagePrefixExcludedFromClear } from '../../../../models/Storage';
import { canViewContentCalendarAndCampaigns, getIndustries, getIndustryName } from '../../../../models/UiUtils';
import { useEmailDeleteTemplate } from '../../../../queries/EmailCampaign/useEmailDeleteTemplate';
import { invalidateMyTemplates } from '../../../../queries/Templates/useMyTemplatesQuery';
import { IBlogTemplateCard, ITemplateCard, KnownCategories } from '../../../../viewmodels/AppViewModels';
import { CampaignTemplateCard } from '../../../components-v2/CampaignTemplateCard';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import {
	BlogCampaignCalendarTemplateCard,
	SocialCampaignCalendarTemplateCard,
} from '../../../components/campaigns/CampaignsByCategorySelector/presentation';
import { CampaignSearchInputField } from '../../../components/campaigns/CampaignSearchInputField';
import { DeleteConfirmation } from '../../../components/DeleteConfirmation';
import { ISelectOption } from '../../../components/DeprecatedSelect';
import { DraggableObserver, DroppableObserver } from '../../../components/helpers/dnd';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { NewCampaignButtonsDropDown } from '../../../components/NewCampaignButtons';

import { baseStyleSheet, bs } from '../../../styles/styles';
import { useContentCalendarTemplateBrowserQueries } from './hooks';
import { styleSheet } from './styles';

export interface IContentCalendarTemplateBrowserComponent {
	getTemplateCards: () => ITemplateCard[];
}

interface IProps extends IImpersonationContextComponentProps {
	dragDropProps: {
		droppableId: string;
		type: string;
		isDragDisabled?: boolean;
		/**
		 * Styles to apply to the template card while dragging
		 */
		draggingStyles?: StyleDeclarationValue[];
	};
	selectedCampaignType?: CampaignType;
	hideCreateCampaignsButton?: boolean;
	isAppDrawer?: boolean;
	onTemplateTypeSelected(templateType: CampaignType): void;
	onCampaignTemplateClicked?(template: Api.ITemplate, schedule?: Api.IScheduledSend, referrer?: string): void;
	campaignTypes: CampaignType[];
	isSuggestionsOn?: boolean;
	selectedUserId?: string | null;
	forUserId?: string | null;
	onBlogPostEditClicked?(e: React.MouseEvent<HTMLElement>, blog: Api.IBlogReportView | IBlogTemplateCard): void;
	onSocialPostEditClicked?(e: React.MouseEvent<HTMLElement>, post: ITemplateCard): void;
}

const ContentCalendarTemplateBrowserBase = React.forwardRef<IContentCalendarTemplateBrowserComponent, IProps>(
	function ContentCalendarTemplateBrowserBase(
		{
			impersonationContext,
			onCampaignTemplateClicked,
			onTemplateTypeSelected,
			dragDropProps,
			selectedCampaignType = CampaignType.Email,
			hideCreateCampaignsButton,
			isAppDrawer,
			campaignTypes,
			isSuggestionsOn = false,
			selectedUserId,
			forUserId,
			onBlogPostEditClicked,
			onSocialPostEditClicked,
		},
		ref
	) {
		const { draggingStyles, ...restDragDropProps } = dragDropProps || {
			droppableId: 'default',
			type: 'default',
		};
		const id = React.useId();
		const userSession = useUserSession();
		const errorMessages = useErrorMessages();
		const logger = useEventLogging('ContentCalendarTemplateBrowser');

		const account = impersonationContext?.isValid ? impersonationContext.account : userSession?.account;
		const isPersonalAccount = !canViewContentCalendarAndCampaigns(userSession, impersonationContext);
		const [industry, setIndustry] = usePersistChangeInBrowserStorage<Api.Industry>({
			storageKey: `${storagePrefixExcludedFromClear}-content-calendar-template-browser-industry`,
			storageType: 'localStorage',
			defaultIfEmpty: account.additionalInfo?.industry ?? Api.Industry.Insurance,
			skipPersistFn: () => impersonationContext?.isValid,
		});

		// When impersonation becomes valid, reset industry to the account's actual industry
		// to avoid stale localStorage values from a previously impersonated account
		React.useEffect(() => {
			if (impersonationContext?.isValid && account?.additionalInfo?.industry) {
				setIndustry(account.additionalInfo.industry);
			}
		}, [impersonationContext?.isValid, account?.additionalInfo?.industry, setIndustry]);

		const defaultCategory = isPersonalAccount ? KnownCategories.MyTemplates : KnownCategories.Featured;
		const [category, setCategory] = React.useState<string>(defaultCategory);
		const [searchQuery, setSearchQuery] = React.useState('');
		const [copyTemplate, setCopyTemplate] = React.useState<Api.ITemplate>(null);
		const [isCopyLinkModalOpen, setIsCopyLinkModalOpen] = React.useState<boolean>(false);
		const [templateToDelete, setTemplateToDelete] = React.useState<ITemplateCard>(null);
		const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = React.useState<boolean>(false);
		const templateTypeOptions = React.useMemo<ISelectOption<CampaignType>[]>(() => {
			return campaignTypes.map(campaignType => {
				return {
					dataContext: campaignType,
					id: `template-type-option-${campaignType}`,
					text: campaignType.valueOf(),
				};
			});
		}, [campaignTypes]);

		const {
			isLoading,
			templatesByCategoryQuery,
			myTemplatesQuery,
			templatesQuery,
			templateSearchQuery,
			templateCategoriesQuery,
		} = useContentCalendarTemplateBrowserQueries({
			templateType: selectedCampaignType,
			searchQuery,
			category,
			industry,
			impersonationContext,
			selectedUserId,
			forUserId,
		});

		const onCampaignSearch = (query: string, newIndustry: Api.Industry) => {
			setIndustry(newIndustry);
			setSearchQuery(query);
		};

		const onCampaignTemplateCardClicked = async (card: ITemplateCard, socialIndicator?: boolean) => {
			const templateId = socialIndicator
				? card.associatedTemplates?.find(x => x.templateType === Api.TemplateType.SocialMediaPost)?.id
				: card.id;
			try {
				const template = await userSession.webServiceHelper.callAsync(
					Api.ImpersonationBroker.composeApiUrl({
						impersonationContext,
						urlPath: `template/${templateId}`,
						queryParams: { expand: 'LastUsedByDate' },
					}),
					'GET'
				);
				onCampaignTemplateClicked?.(template);
			} catch (error) {
				logger.logApiError('LoadTemplate-Error', error);
				errorMessages.pushApiError(error);
			}
		};
		const handleCampaignTypeValueChange = (campaignType: CampaignType) => {
			onTemplateTypeSelected(campaignType);
			setCategory(defaultCategory);
		};
		const handleIndustryValueChange = (i: Api.Industry) => {
			setIndustry(i);
			setCategory(defaultCategory);
		};

		const onCopyDirectLinkClick = (template: Api.ITemplate) => {
			setCopyTemplate(template);
			setIsCopyLinkModalOpen(true);
		};

		// Delete template functionality
		const canDelete = React.useCallback(
			(cardOwnerId: string) => {
				if (userSession.isAdmin) {
					return true;
				}

				if (!cardOwnerId) {
					return false;
				}

				return cardOwnerId === userSession.user?.id;
			},
			[userSession.user?.id, userSession.isAdmin]
		);

		const deleteEmailMutation = useEmailDeleteTemplate({
			onSuccess: () => {
				toast.success('Template deleted successfully');
				// Invalidate queries to refresh the template list
				invalidateMyTemplates({ templateType: Api.TemplateType.Email });
				// Refetch the current queries
				if (category === KnownCategories.MyTemplates && myTemplatesQuery) {
					myTemplatesQuery.refetch();
				}
				if (templatesQuery) {
					templatesQuery.refetch();
				}
				if (templatesByCategoryQuery) {
					templatesByCategoryQuery.refetch();
				}
				if (templateSearchQuery) {
					templateSearchQuery.refetch();
				}
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError('TemplateDelete-Error', error);
				toast.error('Error deleting template');
			},
		});

		const onDeleteClick = (templateCard: ITemplateCard) => {
			setTemplateToDelete(templateCard);
			setIsDeleteConfirmationOpen(true);
		};

		const handleDeleteConfirmation = async (shouldDelete: boolean) => {
			setIsDeleteConfirmationOpen(false);

			if (shouldDelete && templateToDelete) {
				try {
					if (canDelete(templateToDelete?.creator?.id)) {
						await deleteEmailMutation.mutateAsync({
							template: templateToDelete,
						});
					} else if (templateToDelete?.id) {
						// If user can't delete, just hide the template locally
						hideDefaultMessageTemplate(templateToDelete.id);
						toast.success('Template hidden successfully');
					}
				} catch {
					// Error handling is done in the mutation
				}
			}

			setTemplateToDelete(null);
		};

		const templateCards = React.useMemo(() => {
			const templateIdsToRemove = localStorage.getItem(HiddenDefaultMessageTemplateKey);
			if (!searchQuery) {
				if (category === KnownCategories.All && templatesByCategoryQuery) {
					const templatesByCategory = templatesByCategoryQuery.data || {};
					return Object.keys(templatesByCategory).reduce<ITemplateCard[]>((result, curr) => {
						const categoryTemplates = templatesByCategory[curr];
						categoryTemplates.forEach(x => {
							if (!result.find(y => y.id === x.id)) {
								result.push(x);
							}
						});
						return result;
					}, []);
				} else if (category === KnownCategories.MyTemplates && myTemplatesQuery) {
					return myTemplatesQuery.data?.filter(x => !templateIdsToRemove?.includes(x.id));
				} else if (
					(category === KnownCategories.SocialMediaDrafts || category === KnownCategories.BlogPostDrafts) &&
					myTemplatesQuery
				) {
					// Use myTemplatesQuery for drafts (mapped to socialDraftsQuery or blogDraftsQuery in hooks)
					return myTemplatesQuery.data?.filter(x => !templateIdsToRemove?.includes(x.id));
				} else {
					return templatesQuery.data?.filter(x => !templateIdsToRemove?.includes(x.id));
				}
			} else {
				return templateSearchQuery.data?.filter(x => !templateIdsToRemove?.includes(x.id));
			}
		}, [
			category,
			myTemplatesQuery,
			searchQuery,
			templateSearchQuery.data,
			templatesByCategoryQuery,
			templatesQuery.data,
		]);

		const templateCardsRef = React.useRef<ITemplateCard[]>(templateCards);
		templateCardsRef.current = templateCards;
		const categoryOptions = React.useMemo(() => {
			let categoryList: string[] = [KnownCategories.All, KnownCategories.Featured];
			if (
				impersonationContext == null ||
				(impersonationContext?.account?.preferences?.csmViewMyCampaignsEnabled &&
					selectedCampaignType !== CampaignType.Blog)
			) {
				categoryList.push(KnownCategories.MyTemplates);
			}
			if (templateCategoriesQuery.data) {
				categoryList = categoryList.concat(
					templateCategoriesQuery.data.filter(
						c => c && c.trim() !== '' && ![...categoryList, 'Archived', 'Uncategorized'].includes(c)
					)
				);
			}
			return categoryList;
		}, [templateCategoriesQuery.data, impersonationContext, selectedCampaignType]);

		React.useImperativeHandle(ref, () => {
			return {
				getTemplateCards: () => templateCardsRef.current || [],
			};
		}, []);

		const cardCtaText = `View ${selectedCampaignType === CampaignType.Blog ? 'Blog Post' : 'Campaign'}`;
		const industryOptions = getIndustries({
			accountId: account?.id,
			brokerDealer: account?.additionalInfo?.brokerDealer,
		}).map(i => ({
			label: getIndustryName(i),
			value: i,
		}));

		return (
			<div
				className={css(
					isAppDrawer ? styleSheet.ContentCalendarTemplateBrowserAppDrawer : styleSheet.ContentCalendarTemplateBrowser
				)}
			>
				<div className={css(styleSheet.searchCampaignsContainer)}>
					{hideCreateCampaignsButton ? null : (
						<NewCampaignButtonsDropDown
							supportedCampaignTypes={campaignTypes}
							selectedCampaignType={selectedCampaignType}
							dropdownDisabled={isSuggestionsOn}
						/>
					)}
					<CampaignSearchInputField onChange={onCampaignSearch} industry={industry} styles={[bs.flex1]} />
				</div>

				<div className={css(styleSheet.filtersContainer)}>
					{!isPersonalAccount ? (
						<>
							{templateTypeOptions?.length > 1 ? (
								<>
									<label className={css(styleSheet.selectLabel)}>Content Type</label>
									<Select
										value={selectedCampaignType}
										onValueChange={handleCampaignTypeValueChange}
										disabled={isSuggestionsOn}
									>
										<SelectTrigger styles={styleSheet.trigger}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{campaignTypes.map(campaignType => {
												return (
													<SelectItem value={campaignType} key={campaignType}>
														{campaignType.valueOf()}
													</SelectItem>
												);
											})}
										</SelectContent>
									</Select>
								</>
							) : null}
							<label className={css(styleSheet.selectLabel)}>Industry</label>
							<Select items={industryOptions} value={industry} onValueChange={handleIndustryValueChange}>
								<SelectTrigger styles={styleSheet.trigger}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{industryOptions.map(o => (
										<SelectItem key={`industry-options-${id}-${o.value}`} value={o.value}>
											{o.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<label className={css(styleSheet.selectLabel)}>Category</label>
							<Select value={category} onValueChange={setCategory}>
								<SelectTrigger styles={styleSheet.trigger}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{categoryOptions.map(c => {
										return (
											<SelectItem value={c} key={`content-cal-browser-category-${c}`}>
												{c}
											</SelectItem>
										);
									})}
								</SelectContent>
							</Select>
						</>
					) : null}

					{/* @ts-ignore */}
					<DroppableObserver {...restDragDropProps} className={css(bs.overflowAuto)}>
						{(droppableProvided: DroppableProvided) => {
							return (
								<div
									className={css(
										bs.boxBorder,
										bs.flex,
										bs.flexCol,
										bs.overflowAuto,
										bs.flex1,
										bs.gap2,
										isAppDrawer && styleSheet.templateCardsScrollableContainerAppDrawer
									)}
									ref={droppableProvided.innerRef}
									{...droppableProvided.droppableProps}
								>
									{templateCards?.map((card, i) => {
										return (
											<DraggableObserver
												disableInteractiveElementBlocking={true}
												draggableId={card.id}
												index={i}
												isDragDisabled={dragDropProps.isDragDisabled}
												key={card.id}
												type={dragDropProps.type}
												data={{ templateCard: card }}
											>
												{(draggableProvided: DraggableProvided, draggableSnapshot: DraggableStateSnapshot) => {
													return (
														<>
															<div
																className={css(bs.boxBorder)}
																ref={draggableProvided.innerRef}
																{...draggableProvided.draggableProps}
																{...draggableProvided.dragHandleProps}
															>
																{selectedCampaignType === CampaignType.Email ? (
																	<CampaignTemplateCard
																		card={card}
																		className={css(styleSheet.templateCardContainer)}
																		copyMenu={
																			<Dropdown>
																				<DropdownEllipsisTrigger styles={[bs.selfEnd]} />
																				<DropdownContent>
																					<DropdownItem onClick={() => onCopyDirectLinkClick(card)}>
																						Copy Direct Link
																					</DropdownItem>
																				</DropdownContent>
																			</Dropdown>
																		}
																		ctaText={cardCtaText}
																		key={card.id}
																		isAccountTemplate={category === KnownCategories.MyTemplates}
																		onCtaClicked={() => onCampaignTemplateCardClicked(card)}
																		onDelete={() => onDeleteClick(card)}
																		onSocialClicked={() => onCampaignTemplateCardClicked(card, true)}
																		isFeatured={category === KnownCategories.Featured}
																		styles={[
																			draggableSnapshot.isDragging ? styleSheet.templateCardDragging : null,
																			draggableSnapshot.isDragging && draggingStyles ? draggingStyles : null,
																		]}
																	/>
																) : null}
																{selectedCampaignType === CampaignType.Blog ? (
																	<BlogCampaignCalendarTemplateCard
																		card={card}
																		key={card.id}
																		isFeatured={categoryOptions.find(x => x === category) === 'Featured'}
																		onCtaClicked={ev => {
																			if (card.isDraft) {
																				onBlogPostEditClicked?.(ev, card as IBlogTemplateCard);
																				return;
																			}
																			onCampaignTemplateCardClicked(card);
																		}}
																	/>
																) : null}
																{selectedCampaignType === CampaignType.Social ? (
																	<SocialCampaignCalendarTemplateCard
																		card={card}
																		key={card.id}
																		isFeatured={categoryOptions.find(x => x === category) === 'Featured'}
																		onCtaClicked={ev => {
																			if (card.isDraft) {
																				onSocialPostEditClicked?.(ev, card);
																				return;
																			}
																			onCampaignTemplateCardClicked(card);
																		}}
																	/>
																) : null}
															</div>
															{draggableSnapshot.isDragging && draggableProvided?.placeholder}
														</>
													);
												}}
											</DraggableObserver>
										);
									})}
									{isLoading ? <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' /> : null}
									<CopyTemplateLinkModal
										isOpen={isCopyLinkModalOpen}
										onRequestClose={() => {
											setIsCopyLinkModalOpen(false);
											setCopyTemplate(null);
										}}
										industry={industry}
										template={copyTemplate}
									/>
									<DeleteConfirmation
										isOpen={isDeleteConfirmationOpen}
										title='Delete Template'
										bodyText={`Are you sure you want to delete the template "${templateToDelete?.name || 'Custom Post'}"? This action cannot be undone.`}
										destructiveButtonText='Delete'
										onFinish={handleDeleteConfirmation}
										showSpinner={deleteEmailMutation.isPending}
									/>
								</div>
							);
						}}
					</DroppableObserver>
				</div>
			</div>
		);
	}
);

const ContentCalendarTemplateBrowserAsObserver = observer(ContentCalendarTemplateBrowserBase);
export const ContentCalendarTemplateBrowser = inject(ImpersonationContextKey)(ContentCalendarTemplateBrowserAsObserver);
